import { createAction, props } from '@ngrx/store';
import {
  AddRemediationSolutionDTO,
  ConnectMetadataResponse,
  ConnectionStatusResponse,
  ExistingSolution,
  IntegrationDetails,
  IntegrationStatus,
  IntegrationSubSidebarMode,
  IntegrationSubmitPayload,
  RequiredSolution,
  ToolConnectionStatus,
  ToolsServicesOverviewResponse,
  UserConnectStartResponse,
  ViewRemediationSolutionDTO,
} from 'projects/shared/src/lib/core/interfaces';
import { baseProps } from 'projects/shared/src/lib/core/store/actions-base';

export const fetchToolsAndServicesOverview = createAction(
  '[@diso/tools-services] fetchToolsAndServicesOverview',
  baseProps(),
);

export const fetchToolsAndServicesOverviewSuccess = createAction(
  '[@diso/tools-services] fetchToolsAndServicesOverviewSuccess',
  props<{ toolsAndServicesOverview: ToolsServicesOverviewResponse }>(),
);

export const fetchToolsAndServicesOverviewFailure = createAction(
  '[@diso/tools-services] fetchToolsAndServicesOverviewFailure',
);

export const hideMessageSummary = createAction(
  '[@diso/tools-services] hideMessageSummary',
  props<{ messageType: 'tools' | 'services' }>(),
);

export const toggleAddToStackSidebar = createAction(
  '[@diso/tools-services] showAddToStackSidebar',
  props<{
    show: boolean;
    sidebarType: 'tool' | 'service';
    item?: RequiredSolution;
  }>(),
);

export const fetchVendors = createAction(
  '[@diso/tools-services] fetchVendors',
  props<{ solutionType: 'Tool' | 'Service' }>(),
);

export const fetchVendorsSuccess = createAction(
  '[@diso/tools-services] fetchVendorsSuccess',
  props<{ vendors: { id: string; name: string }[] }>(),
);

export const fetchVendorsFailure = createAction(
  '[@diso/tools-services] fetchVendorsFailure',
);

export const fetchProductOptions = createAction(
  '[@diso/tools-services] fetchProductOptions',
  props<{ vendorId: string; solutionType: 'Tool' | 'Service' }>(),
);

export const fetchProductOptionsSuccess = createAction(
  '[@diso/tools-services] fetchProductOptionsSuccess',
  props<{
    productOptions: {
      id: string;
      name: string;
      exists: boolean;
      currentlyInStack: boolean;
    }[];
  }>(),
);

export const fetchProductOptionsFailure = createAction(
  '[@diso/tools-services] fetchProductOptionsFailure',
);

export const fetchLicenseOptions = createAction(
  '[@diso/tools-services] fetchLicenseOptions',
  props<{ solutionId: string }>(),
);

export const fetchLicenseOptionsSuccess = createAction(
  '[@diso/tools-services] fetchLicenseOptionsSuccess',
  props<{
    licenseOptions: { id: string; name: string; fitsSecurityPlan?: boolean }[];
    planWindow: {
      activePlanEndUtc?: string;
      approvedEndUtc?: string;
      approvedStartUtc?: string;
      hasActivePlan: boolean;
      hasApprovedPlan: boolean;
    };
  }>(),
);

export const fetchLicenseOptionsFailure = createAction(
  '[@diso/tools-services] fetchLicenseOptionsFailure',
);

export const toggleViewDetailsSidebar = createAction(
  '[@diso/tools-services] toggleViewDetailsSidebar',
  props<{
    show: boolean;
    sidebarType: 'tool' | 'service';
    item?: ExistingSolution;
  }>(),
);

export const toggleRemoveSubSidebar = createAction(
  '[@diso/tools-services] toggleRemoveSubSidebar',
  props<{ show: boolean; itemType?: 'solution' | 'integration' }>(),
);

export const toggleRenewLicenseSubSidebar = createAction(
  '[@diso/tools-services] toggleRenewLicenseSubSidebar',
  props<{ show: boolean }>(),
);

export const fetchViewDetailsItem = createAction(
  '[@diso/tools-services] fetchViewDetailsItem',
  props<{ sidebarType: 'tool' | 'service' }>(),
);

export const fetchViewDetailsItemSuccess = createAction(
  '[@diso/tools-services] fetchViewDetailsItemSuccess',
  props<{ viewDetailsItem: ExistingSolution | undefined }>(),
);

export const fetchViewDetailsItemFailure = createAction(
  '[@diso/tools-services] fetchViewDetailsItemFailure',
);

export const checkRemoveSubSidebar = createAction(
  '[@diso/tools-services] checkRemoveSubSidebar',
  props<{ solutionId: string }>(),
);

export const checkRemoveSubSidebarSuccess = createAction(
  '[@diso/tools-services] checkRemoveSubSidebarSuccess',
  props<{
    customerSolutionId: string;
    blocked: boolean;
    message: {
      messageType: string;
      titleId: string;
      contentId: string;
      integrationRemovalId?: string;
    };
  }>(),
);

export const checkRemoveSubSidebarFailure = createAction(
  '[@diso/tools-services] checkRemoveSubSidebarFailure',
);

export const removeSolution = createAction(
  '[@diso/tools-services] removeSolution',
  baseProps<{ solutionId: string; sidebarType: 'tool' | 'service' }>(),
);

export const removeSolutionSuccess = createAction(
  '[@diso/tools-services] removeSolutionSuccess',
  props<{ solutionId: string }>(),
);

export const removeSolutionFailure = createAction(
  '[@diso/tools-services] removeSolutionFailure',
);

export const removeIntegration = createAction(
  '[@diso/tools-services] removeIntegration',
  baseProps<{ solutionId: string }>(),
);

export const removeIntegrationSuccess = createAction(
  '[@diso/tools-services] removeIntegrationSuccess',
  props<{ solutionId: string }>(),
);

export const removeIntegrationFailure = createAction(
  '[@diso/tools-services] removeIntegrationFailure',
);

export const fetchCategoryOptions = createAction(
  '[@diso/tools-services] fetchCategoryOptions',
);

export const fetchCategoryOptionsSuccess = createAction(
  '[@diso/tools-services] fetchCategoryOptionsSuccess',
  props<{
    categoryOptions: {
      categories: {
        categoryId: string;
        categoryName: string;
        features: any[];
      }[];
    };
  }>(),
);

export const addEditRemediationSolution = createAction(
  '[@diso/tools-services] addEditRemediationSolution',
  baseProps<{
    remediationSolution: AddRemediationSolutionDTO;
  }>(),
);

export const addEditRemediationSolutionSuccess = createAction(
  '[@diso/tools-services] addEditRemediationSolutionSuccess',
  props<{ remediationSolution: AddRemediationSolutionDTO }>(),
);

export const addEditRemediationSolutionFailure = createAction(
  '[@diso/tools-services] addEditRemediationSolutionFailure',
);

export const setSelectedCategoryId = createAction(
  '[@diso/tools-services] setSelectedCategoryId',
  props<{ categoryId: string }>(),
);

export const fetchRemediationSolutionDetails = createAction(
  '[@diso/tools-services] fetchRemediationSolutionDetails',
  baseProps<{ solutionId: string }>(),
);

export const fetchRemediationSolutionDetailsSuccess = createAction(
  '[@diso/tools-services] fetchRemediationSolutionDetailsSuccess',
  props<{ remediationSolution: ViewRemediationSolutionDTO }>(),
);

export const fetchRemediationSolutionDetailsFailure = createAction(
  '[@diso/tools-services] fetchRemediationSolutionDetailsFailure',
);

export const highlightRemovedItem = createAction(
  '[@diso/tools-services] highlightRemovedItem',
  props<{ highlight: boolean; solutionId: string }>(),
);

export const setCurrentStep = createAction(
  '[@diso/tools-services] setCurrentStep',
  props<{ step: number }>(),
);

export const openIntegrationSubSidebar = createAction(
  '[@diso/tools-services] openIntegrationSubSidebar',
  props<{
    show: boolean;
    sidebarType: 'tool' | 'service';
    mode?: IntegrationSubSidebarMode;
    solutionId?: string;
    solutionName?: string;
    selectedLicenseId?: string;
  }>(),
);

export const fetchIntegrationDetails = createAction(
  '[@diso/tools-services] fetchIntegrationDetails',
  props<{ solutionId: string; sidebarType: 'tool' | 'service' }>(),
);

export const fetchIntegrationDetailsSuccess = createAction(
  '[@diso/tools-services] fetchIntegrationDetailsSuccess',
  props<{ integrationDetails: IntegrationDetails }>(),
);

export const fetchIntegrationDetailsFailure = createAction(
  '[@diso/tools-services] fetchIntegrationDetailsFailure',
);

/** GET connect-metadata to read `connectSupported` for add-to-stack (solution picker). */
export const checkIntegrationSupportForSolution = createAction(
  '[@diso/tools-services] checkIntegrationSupportForSolution',
  props<{ solutionId: string; sidebarType: 'tool' | 'service' }>(),
);

export const checkIntegrationSupportForSolutionSuccess = createAction(
  '[@diso/tools-services] checkIntegrationSupportForSolutionSuccess',
  props<{ solutionId: string; connectSupported: boolean }>(),
);

export const checkIntegrationSupportForSolutionFailure = createAction(
  '[@diso/tools-services] checkIntegrationSupportForSolutionFailure',
  props<{ solutionId: string }>(),
);

export const fetchConnectMetadataSuccess = createAction(
  '[@diso/tools-services] fetchConnectMetadataSuccess',
  props<{ connectMetadata: ConnectMetadataResponse }>(),
);

export const clearAddToStackIntegrationSupport = createAction(
  '[@diso/tools-services] clearAddToStackIntegrationSupport',
);

/**
 * Add-to-stack creation flow: stash the values entered in the integration sub-sidebar
 * until the new customer solution is created. `solutionId` here is the catalog id of the
 * selected product (used only to scope the draft to a specific product selection).
 */
export const setAddToStackIntegrationDraft = createAction(
  '[@diso/tools-services] setAddToStackIntegrationDraft',
  props<{
    solutionId: string;
    values: Record<string, string | boolean>;
  }>(),
);

export const clearAddToStackIntegrationDraft = createAction(
  '[@diso/tools-services] clearAddToStackIntegrationDraft',
);

export const submitIntegrationConnection = createAction(
  '[@diso/tools-services] submitIntegrationConnection',
  baseProps<{
    sidebarType: 'tool' | 'service';
    mode: 'add' | 'edit';
    payload: IntegrationSubmitPayload;
    /**
     * Submitted from the open integration sub-sidebar: freeze the UI and keep the
     * sub-sidebar open until the integration status is known. Omitted by the
     * add-to-stack save flow, which fires the connect call after its sidebar is gone.
     */
    awaitStatus?: boolean;
  }>(),
);

export const submitIntegrationConnectionSuccess = createAction(
  '[@diso/tools-services] submitIntegrationConnectionSuccess',
);

export const submitIntegrationConnectionFailure = createAction(
  '[@diso/tools-services] submitIntegrationConnectionFailure',
);

export const startUserConnectFlow = createAction(
  '[@diso/tools-services] startUserConnectFlow',
  baseProps<{
    sidebarType: 'tool' | 'service';
    mode: 'add' | 'edit';
    payload: {
      solutionId: string;
      integrationServiceId: string;
      values: Record<string, string | boolean | string[]>;
      selectedLicenseId: string;
    };
  }>(),
);

export const startUserConnectFlowSuccess = createAction(
  '[@diso/tools-services] startUserConnectFlowSuccess',
  props<{ response: UserConnectStartResponse }>(),
);

export const startUserConnectFlowFailure = createAction(
  '[@diso/tools-services] startUserConnectFlowFailure',
);

export const clearIntegrationConnectionFailure = createAction(
  '[@diso/tools-services] clearIntegrationConnectionFailure',
);

export const submitUserConnectCallback = createAction(
  '[@diso/tools-services] submitUserConnectCallback',
  props<{ state: string; providerCallback: Record<string, string> }>(),
);

export const submitUserConnectCallbackSuccess = createAction(
  '[@diso/tools-services] submitUserConnectCallbackSuccess',
  props<{
    toolId: string;
    integrationServiceId: string;
    connectionStatus: ToolConnectionStatus;
  }>(),
);

export const submitUserConnectCallbackFailure = createAction(
  '[@diso/tools-services] submitUserConnectCallbackFailure',
  props<{ safeErrorMessage?: string }>(),
);

export const startConnectionStatusPolling = createAction(
  '[@diso/tools-services] startConnectionStatusPolling',
  props<{ toolId: string; integrationServiceId?: string }>(),
);

export const stopConnectionStatusPolling = createAction(
  '[@diso/tools-services] stopConnectionStatusPolling',
);

export const pollConnectionStatusSuccess = createAction(
  '[@diso/tools-services] pollConnectionStatusSuccess',
  props<{ result: ConnectionStatusResponse }>(),
);

export const pollConnectionStatusFailure = createAction(
  '[@diso/tools-services] pollConnectionStatusFailure',
);

export const setIntegrationStatus = createAction(
  '[@diso/tools-services] setIntegrationStatus',
  props<{ solutionId: string; status: IntegrationStatus }>(),
);

export const setIntegrationSubSidebarMode = createAction(
  '[@diso/tools-services] setIntegrationSubSidebarMode',
  props<{ mode: IntegrationSubSidebarMode }>(),
);

/**
 * The connect call went through: hold the frozen UI until the integration manager
 * reports a terminal connection status.
 */
export const awaitIntegrationStatus = createAction(
  '[@diso/tools-services] awaitIntegrationStatus',
  props<{ sidebarType: 'tool' | 'service'; solutionId: string }>(),
);

/** A SignalR change notification arrived for tools & services — re-read the status now. */
export const integrationStatusNotificationReceived = createAction(
  '[@diso/tools-services] integrationStatusNotificationReceived',
  props<{
    solutionId?: string;
    /** System message id explaining a failed connect, when the notification carries one. */
    messageId?: string;
  }>(),
);

export const integrationStatusResolved = createAction(
  '[@diso/tools-services] integrationStatusResolved',
  props<{
    sidebarType: 'tool' | 'service';
    solutionId: string;
    /** The solution's status as read once the connect test finished. */
    integrationStatus: IntegrationStatus;
    /** System message id for the failure; the default failure text is shown without it. */
    messageId?: string;
  }>(),
);
